import { msg } from "@omegagrid/localize";
import { MenuEvent, MenuItem } from "@omegagrid/core";
import { NodeModel, NodeSource, TreeSource } from "@omegagrid/tree";
import { Resource } from "@reporting/rp-types/ext";
import { getEditorComponentType, getFileExtensionInfo } from "../utils";
import rp, { BrowserAdapter, BrowserOptions, GridEditor, helpers } from "@reporting/rp-app-host";
import { css, html, render } from "lit";
import { ExtensionApplication } from "..";

export class FileBrowserAdapter extends BrowserAdapter {

	constructor(readonly idProject: number) { super() }

	async getTree(): Promise<TreeSource> {
		const treeItems: NodeSource[] = [];
		const files = this.idProject ? await rp.ms('extensions').get<Resource[]>(`resources`, {
			fields: ['id_custom_resource', 'name', 'queries', 'module'],
			where: {id_custom_page: this.idProject},
			order: [['name', 'asc']]
		}) : [];

		files.sort((a, b) => helpers.comparePaths(a.name, b.name));

		let parents: string[] = [];
		files.forEach(file => {
			const chunks = file.name.split('/');
			const filename = chunks.pop();

			for (let i = helpers.commonPathLength(chunks, parents); i < chunks.length; i++) {
				const dir = chunks[i];

				treeItems.push({
					k: dir,
					v: dir,
					l: i,
					collapsed: true,
					folder: BrowserAdapter.folderOptions
				});
			}

			const extensionInfo = getFileExtensionInfo(file.name);
			treeItems.push({
				k: `extensions-${getEditorComponentType(file.name)}-${file.id_custom_resource}`,
				v: filename,
				l: chunks.length,
				icon: extensionInfo.icon,
				props: { file }
			});

			parents = chunks;
		});

		if (treeItems.length == 0) {
			treeItems.push({
				k: 'extensions-fileForm',
				v: `<${msg(`New file`)}>`,
				icon: 'plus'
			});
		}

		return treeItems;
	}

	getMenuItems(node: NodeModel): MenuItem[] {
		const file = node.properties?.file as File;
		if (!file) return [];
		const items: MenuItem[] = [];
		items.push({key: 'open', value: msg('Open'), icon: 'book-open'});
		if (getEditorComponentType(file.name) == 'sheetEditor') {
			items.push({key: 'source', value: msg('Source'), icon: 'code'});
		}
		items.push({key: 'properties', value: msg('Properties'), icon: 'pen-to-square'});
		items.push({key: 'dependencies', value: msg('SQL Dependencies'), icon: 'diagram-predecessor'});
		items.push({key: 'duplicate', value: msg('Duplicate'), icon: 'copy'});
		items.push({key: 'delete', value: msg('Delete'), icon: 'trash', color: '#ff0000'});
		return items;
	}

	async onAction(key: string, node: NodeModel) {
		const file = node.properties.file as Resource;
		switch (key) {
			case 'open':
				rp.host.open(node.key, {title: node.value});
				return;
			case 'source':
				rp.host.open(`extensions-file-${file.id_custom_resource}`, {title: node.value});
				return;
			case 'properties':
				rp.host.openModalSidebar({
					id: `extensions-fileForm-${file.id_custom_resource}`,
					caption: file.name
				});
				return;
			case 'duplicate':
				rp.host.openModalSidebar({
					id: `extensions-fileForm-${file.id_custom_resource}-copy`,
					caption: `${msg('Copy')}: ${file.name}`
				});
				break;
			case 'delete':
				if (!file) return;
				const command = await rp.host.openMenu({
					content: 'yes_no',
					text: `Are you sure you want to delete '#${file.id_custom_resource} ${file.name}'?`
				});
				if (command?.id == 'Y') {
					await rp.ms('extensions').delete(`resources/${file.id_custom_resource}`);
					this.browser.deleteNode(node);
				}
				return;
			case 'dependencies':
				const editor = await rp.host.open<GridEditor>(`extensions-sqlDependencies-${this.idProject}`, {
					title: msg('SQL Dependencies')
				});
				setTimeout(() => {
					editor.grid.filtering.filter({c: 1, term: file.id_custom_resource});
				}, 100); // wait for grid model to initialize
				return;
		}
	}

	getOptions(): Partial<BrowserOptions> {
		return this.createOptions({
			selectMode: 'none',
			expanders: false,
			contextMenuButton: true,
			customStyle: css`
				.item-text {
					flex: 1;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}
				og-dropdown-menu {
					flex: 0 0 25px;
				}
			`,
			nodeRenderer: (div: HTMLDivElement, node: NodeModel) => {
				const file = node.properties?.file as Resource;

				if (!file) {
					div.textContent = node.value;
					return;
				}

				div.style.display = 'flex';
				div.style.flexDirection = 'row';

				const defaultResourceId = rp.applications.get<ExtensionApplication>('extensions')?.activeProject?.id_custom_resource;

				render(html`
					<div class="item-text">
						${node.value}${file.module ? ` (${file.module})` : ''}
						${file.id_custom_resource === defaultResourceId ? html`<og-icon icon="house" style="display: inline"></og-icon>` : ''}
					</div>

					${file.queries?.length ? html`
						<og-dropdown-menu
							@mousedown="${(e: MouseEvent) => e.stopPropagation()}"
							noCaret
							dynamicWidth
							icon="database"s
							color="transparent"
							.label="${file.queries.length.toString()}"
							.items="${file.queries.map(query => ({key: query.id_thread, value: query.description}))}"
							@menu.select="${(e: MenuEvent) => rp.host.open(`extensions-query-${e.item.key}`, {title: e.item.value})}">
						</og-dropdown-menu>
					` : ''}
				`, div);
			},
		});
	}
}
